import React, { useState } from "react";
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Collapse,
  Divider, LinearProgress, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, Tooltip, Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  COMPONENT_LABELS, NON_PAYABLE_COMPONENTS, formatCents,
  useComputeAssessment, useTaxLines, useTaxSummary,
} from "../../../api/fiscalite";

interface Props {
  fleetId: number;
  fiscalYear: number;
  onGoToReadiness: () => void;
}

/**
 * Annual summary.
 *
 * Three things this screen refuses to do, all for the same reason — a tax
 * figure without its context is a liability:
 *
 *  - it never shows a total without the computation date, the rule-set version
 *    and the share of the fleet actually covered;
 *  - it never sums taxes payable with add-backs to taxable profit;
 *  - it never presents an incomplete assessment as a final figure.
 */
export const Synthese: React.FC<Props> = ({ fleetId, fiscalYear, onGoToReadiness }) => {
  const { t } = useTranslation();
  const { summary, isLoading, refetch } = useTaxSummary(fleetId, fiscalYear);
  const compute = useComputeAssessment(fleetId);
  const [expandedLine, setExpandedLine] = useState<number | null>(null);

  const { linesPayload } = useTaxLines(fleetId, summary?.assessment_id, { per_page: 200 } as any);
  const lines = linesPayload?.lines?.data ?? [];

  if (isLoading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>;
  }

  const notComputed = summary?.status === "NOT_COMPUTED";
  const coverage = summary?.coverage;
  const ratio = coverage ? coverage.ratio : 0;

  const runCompute = async () => {
    await compute.mutateAsync({ fiscal_year: fiscalYear });
    // The endpoint answers 202; the figure appears once the worker has run.
    setTimeout(() => refetch(), 4000);
  };

  if (notComputed) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t("tax.notComputed", "Aucun calcul disponible pour {{year}}", { year: fiscalYear })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {summary?.readiness?.can_compute
              ? t("tax.canCompute", "Les donnees necessaires sont disponibles. Lancez le calcul pour obtenir l'estimation.")
              : t("tax.cannotCompute", "Les donnees fiscales des vehicules sont incompletes. Completez-les avant de lancer un calcul.")}
          </Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              onClick={runCompute}
              disabled={compute.isPending || !summary?.readiness?.can_compute}
            >
              {compute.isPending ? t("common.loading", "Chargement...") : t("tax.compute", "Lancer le calcul")}
            </Button>
            <Button variant="outlined" onClick={onGoToReadiness}>
              {t("tax.viewGaps", "Voir les donnees manquantes")}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  const totals = summary?.totals_by_component ?? {};
  const payable = Object.entries(totals).filter(([key]) => !NON_PAYABLE_COMPONENTS.includes(key));
  const nonPayable = Object.entries(totals).filter(([key]) => NON_PAYABLE_COMPONENTS.includes(key));

  return (
    <Stack spacing={3}>
      {/* Draft rule sets and partial coverage are surfaced ABOVE the number,
          never as a footnote under it. */}
      {summary?.warnings?.map((warning) => (
        <Alert key={warning.code} severity={warning.code === "DRAFT_RULE_SET" ? "warning" : "info"}>
          {warning.message}
        </Alert>
      ))}

      {ratio < 1 && (
        <Alert
          severity="warning"
          action={<Button size="small" onClick={onGoToReadiness}>{t("tax.complete", "Completer")}</Button>}
        >
          {t("tax.partialCoverage", {
            defaultValue:
              "{{complete}} vehicules sur {{total}} sont calcules. Le total ci-dessous est donc sous-estime.",
            complete: coverage?.vehicles_complete ?? 0,
            total: coverage?.vehicles_total ?? 0,
          })}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
            <Box>
              <Typography variant="overline" color="text.secondary">
                {t("tax.taxesPayable", "Taxes annuelles estimees")}
              </Typography>
              <Typography variant="h3" fontWeight={700}>
                {formatCents(summary?.total_cents)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("tax.computedAt", "Calcule le")}{" "}
                {summary?.computed_at ? new Date(summary.computed_at).toLocaleString("fr-FR") : "-"}
                {" - "}
                {t("tax.revision", "Revision")} {summary?.revision}
                {summary?.rule_set_ids?.length
                  ? ` - ${t("tax.ruleSets", "Baremes")} #${summary.rule_set_ids.join(", #")}`
                  : ""}
              </Typography>
            </Box>

            <Stack alignItems="flex-end" spacing={1}>
              <Chip
                size="small"
                color={summary?.filing_ready ? "success" : "warning"}
                label={
                  summary?.filing_ready
                    ? t("tax.filingReady", "Exploitable pour la declaration")
                    : t("tax.notFilingReady", "Non exploitable en l'etat")
                }
              />
              <Button size="small" variant="outlined" onClick={runCompute} disabled={compute.isPending}>
                {t("tax.recompute", "Recalculer")}
              </Button>
            </Stack>
          </Stack>

          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={ratio * 100} />
            <Typography variant="caption" color="text.secondary">
              {t("tax.coverage", "Couverture")} : {Math.round(ratio * 100)}%
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {t("tax.payableBreakdown", "Taxes a payer")}
            </Typography>
            {payable.length === 0 && (
              <Typography variant="body2" color="text.secondary">{t("common.noData", "Aucune donnee")}</Typography>
            )}
            {payable.map(([key, cents]) => (
              <Stack key={key} direction="row" justifyContent="space-between" sx={{ py: 0.75 }}>
                <Typography variant="body2">{COMPONENT_LABELS[key] ?? key}</Typography>
                <Typography variant="body2" fontWeight={600}>{formatCents(cents as number)}</Typography>
              </Stack>
            ))}
          </CardContent>
        </Card>

        <Card sx={{ flex: 1 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              {t("tax.nonPayable", "Reintegrations et avantages")}
            </Typography>
            {/* Kept visually separate on purpose: these are not amounts to pay. */}
            <Typography variant="caption" color="text.secondary">
              {summary?.non_payable_note}
            </Typography>
            <Divider sx={{ my: 1 }} />
            {nonPayable.length === 0 && (
              <Typography variant="body2" color="text.secondary">{t("common.noData", "Aucune donnee")}</Typography>
            )}
            {nonPayable.map(([key, cents]) => (
              <Stack key={key} direction="row" justifyContent="space-between" sx={{ py: 0.75 }}>
                <Typography variant="body2">{COMPONENT_LABELS[key] ?? key}</Typography>
                <Typography variant="body2" fontWeight={600}>{formatCents(cents as number)}</Typography>
              </Stack>
            ))}
          </CardContent>
        </Card>
      </Stack>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t("tax.lines", "Detail par vehicule")}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("tax.vehicle", "Vehicule")}</TableCell>
                <TableCell>{t("tax.component", "Composante")}</TableCell>
                <TableCell>{t("tax.period", "Periode")}</TableCell>
                <TableCell align="right">{t("tax.proration", "Prorata")}</TableCell>
                <TableCell align="right">{t("tax.amount", "Montant")}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line: any) => (
                <React.Fragment key={line.id}>
                  <TableRow hover>
                    <TableCell>#{line.vehicle_id}</TableCell>
                    <TableCell>{COMPONENT_LABELS[line.component] ?? line.component}</TableCell>
                    <TableCell>{line.period_start} - {line.period_end}</TableCell>
                    <TableCell align="right">{Math.round(line.proration * 100)}%</TableCell>
                    <TableCell align="right">
                      {line.line_status === "INCOMPLETE" ? (
                        <Tooltip title={t("tax.incompleteTooltip", "Non calcule : donnees manquantes")}>
                          <Chip size="small" color="warning" label={t("tax.incomplete", "Incomplet")} />
                        </Tooltip>
                      ) : line.line_status === "EXEMPT" ? (
                        <Chip size="small" color="success" label={t("tax.exempt", "Exonere")} />
                      ) : (
                        formatCents(line.amount_cents)
                      )}
                    </TableCell>
                    <TableCell align="right">
                      {/* Explainability is always one click away. A figure the
                          customer cannot audit is a figure they will not file. */}
                      <Button size="small" onClick={() => setExpandedLine(expandedLine === line.id ? null : line.id)}>
                        {t("tax.why", "Pourquoi ?")}
                      </Button>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
                      <Collapse in={expandedLine === line.id} unmountOnExit>
                        <Box sx={{ bgcolor: "action.hover", p: 2 }}>
                          <Typography variant="caption" display="block" fontWeight={600}>
                            {t("tax.formula", "Formule")}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1, fontFamily: "monospace" }}>
                            {line.explanation?.formula}
                          </Typography>

                          <Typography variant="caption" display="block" fontWeight={600}>
                            {t("tax.inputs", "Donnees utilisees")}
                          </Typography>
                          <Typography variant="body2" sx={{ mb: 1, fontFamily: "monospace", fontSize: 12 }}>
                            {JSON.stringify(line.explanation?.inputs)}
                          </Typography>

                          {line.explanation?.legal_ref && (
                            <>
                              <Typography variant="caption" display="block" fontWeight={600}>
                                {t("tax.legalRef", "Reference")}
                              </Typography>
                              <Typography variant="body2">{line.explanation.legal_ref}</Typography>
                            </>
                          )}

                          {!!line.explanation?.warnings?.length && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                              {line.explanation.warnings.join(" - ")}
                            </Alert>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Alert severity="info">{summary?.disclaimer}</Alert>
    </Stack>
  );
};
